import Icon from '@/components/ui/AppIcon';

interface PaymentDetail {
  id: string
  label: string
  value: string
}

interface TimelineStep {
  id: string
  icon: string
  label: string
  duration: string
}

const PaymentTimelineSection = () => {
  const paymentDetails: PaymentDetail[] = [
    { id: 'payment_fee', label: 'Payment Plan Fee', value: '$1,750' },
    { id: 'payment_down', label: 'Down Payment', value: '60% to start' },
    { id: 'payment_final', label: 'Final Payment', value: '40% before delivery' },
  ]

  const timelineSteps: TimelineStep[] = [
    { id: 'timeline_production', icon: 'WrenchScrewdriverIcon', label: 'Production', duration: '4–6 weeks' },
    { id: 'timeline_delivery', icon: 'TruckIcon', label: 'Delivery', duration: '30–45 days' },
  ]

  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Section Header */}
        <div className="reveal text-center mb-16">
          <h2 className="text-5xl md:text-6xl font-bold font-montserrat text-foreground mb-6">
            Payment Plans & Timelines
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Flexible payment options and transparent timelines for your container project.
          </p>
        </div>

        {/* Split Cards */}
        <div className="grid md:grid-cols-2 gap-8">
          {/* Payment Plans Card */}
          <div className="reveal container-card p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Icon name="CreditCardIcon" size={24} className="text-primary" />
              </div>
              <h3 className="text-2xl font-bold font-montserrat text-foreground">
                Payment Plans
              </h3>
            </div>

            <div className="space-y-6">
              {paymentDetails.map((detail) => (
                <div key={detail.id} className="flex items-center justify-between pb-4 border-b border-gray-200 last:border-0">
                  <span className="text-muted-foreground">{detail.label}</span>
                  <span className="text-xl font-bold text-foreground">{detail.value}</span>
                </div>
              ))}
            </div>

            <div className="mt-8 p-4 bg-primary/5 rounded-xl">
              <p className="text-sm text-muted-foreground flex items-start gap-2">
                <Icon name="InformationCircleIcon" size={20} className="text-primary flex-shrink-0 mt-0.5" />
                <span>Payment plan available for qualified buyers. Contact us for details.</span>
              </p>
            </div>
          </div>

          {/* Timeline Card */}
          <div className="reveal container-card p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Icon name="ClockIcon" size={24} className="text-primary" />
              </div>
              <h3 className="text-2xl font-bold font-montserrat text-foreground">
                Time Frames
              </h3>
            </div>

            <div className="space-y-8">
              {timelineSteps.map((step, index) => (
                <div key={step.id} className="relative">
                  {/* Connector Line */}
                  {index < timelineSteps.length - 1 && (
                    <div className="absolute left-6 top-14 w-0.5 h-12 bg-gray-200" />
                  )}

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center flex-shrink-0">
                      <Icon name={step.icon as any} size={24} className="text-primary-foreground" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-lg font-semibold text-foreground mb-1">
                        {step.label}
                      </h4>
                      <p className="text-2xl font-bold text-primary">{step.duration}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 p-4 bg-success/5 rounded-xl">
              <p className="text-sm text-muted-foreground flex items-start gap-2">
                <Icon name="CheckCircleIcon" size={20} className="text-success flex-shrink-0 mt-0.5" />
                <span>Total timeline: 8-11 weeks from order to delivery</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default PaymentTimelineSection